//LIBRARIES
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { Formik, Form, Field } from 'formik';
import FlatButton from '@material-ui/core/Button';
import isEmpty from 'lodash/isEmpty';
import trim from 'lodash/trim';

//HELPERS
import { colors } from 'helpers/ColorHelper';
import { formatMoney } from 'helpers/Formatter';
import { AccentColor } from 'helpers/AccentColorHelper';
import * as validator from 'helpers/Validator';
import { RenderIfToggleOn } from 'components/shared/ToggleRenderer';
import { getTranslatedString } from 'helpers/i18n';
import { media } from 'helpers/MediaHelper';

//COMPONENTS
import { PaysafeFields } from './PaysafeFields';
import { Input } from 'components/ui/Input';

const StyledH3 = styled.h3`
  text-align: center;
`;

const StyledContainer = styled.div`
  display: ${(props) => (props.isHidden ? 'none' : 'block')};
  ${media.mobile`
    height: 100%;
  `};
`;

const StyledForm = styled(Form)`
  ${media.mobile`
    height: 100%;
    display:flex;
    flex-direction: column;
  `};
`;

const StyledFormCtaContainer = styled.div`
  padding: 1rem 0;
  display: flex;
  justify-content: space-between;
`;

const StyledInput = styled(Input)`
  margin-bottom: 0.5rem;
`;

const StyledSection = styled.div`
  margin-bottom: 0.5rem;
`;

const StyledCardSection = styled.div`
  margin-bottom: 0.5rem;
  ${media.mobile`
    flex: 1;
  `};
`;

const StyledH4 = styled.h4`
  text-align: center;
  background: #f1f1f1;
  padding: 0.5rem 0;
  margin-bottom: 0.5rem;
`;
const StyledAmountOutstandingP = styled.p`
  margin: 1rem 0;
`;

const StyledErrorP = styled.p`
  color: ${colors.YUHU_RED};
`;

const StyledPaymentAmountInfo = styled.p`
  text-align: center;
  padding: 10px 20px 0px;
`;

export const PaysafePaymentForm = ({
  outstandingBalance,
  handleSubmit,
  handleClose,
  paymentAuthorizing,
  isHidden,
  setPaysafeInstance,
  cardNumberError,
  cvvError,
  expiryDateError,
  setCardNumberError,
  setCvvError,
  setExpiryDateError,
  resetPaysafeFieldErrors,
  lockedAmount,
  paymentHold,
  showFeeInfo,
}) => {
  const validatePaymentForm = useCallback(async ({ paymentAmount, cardHolderName, postalCode }) => {
    let errors = {};
    const amountInFloat = parseFloat(String(paymentAmount).replace('$', ''));
    if (isNaN(amountInFloat)) {
      errors.paymentAmount = getTranslatedString('tenantPortal.paysafe.paymentAmountNotNum');
    } else if (amountInFloat < 1) {
      errors.paymentAmount = getTranslatedString(
        'tenantPortal.paysafe.paymentAmountMustBeGreater',
        {
          amount: '$1',
        }
      );
    }
    const required = getTranslatedString('required');
    if (!cardHolderName) errors.cardHolderName = required;

    if (isEmpty(trim(postalCode))) errors.postalCode = required;
    if (postalCode.length > 10) {
      errors.postalCode = getTranslatedString('tenantPortal.paysafe.notValidPostalCode');
    }

    if (Object.keys(errors).length) {
      throw errors;
    }
  }, []);

  return (
    <StyledContainer isHidden={isHidden}>
      <Formik
        initialValues={{
          paymentAmount: outstandingBalance > 0 ? outstandingBalance : 0,
          postalCode: '',
          cardHolderName: '',
        }}
        validate={validatePaymentForm}
        onSubmit={handleSubmit}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {(formikProps) => {
          return (
            <StyledForm>
              <StyledSection>
                <StyledH3>
                  {getTranslatedString('tenantPortal.paysafe.confirmPaymentDetails')}
                </StyledH3>
                <StyledH4>
                  {lockedAmount
                    ? getTranslatedString('tenantPortal.paysafe.paymentAmount')
                    : getTranslatedString('tenantPortal.paysafe.howMuchWouldYouLikeToPay')}
                </StyledH4>
                <RenderIfToggleOn toggle="applications.application_fee" dontRenderIf={!showFeeInfo}>
                  <StyledPaymentAmountInfo>
                    {getTranslatedString('tenantPortal.paysafe.applicationFeeInfo')}
                  </StyledPaymentAmountInfo>
                </RenderIfToggleOn>
                {paymentHold && (
                  <StyledAmountOutstandingP>
                    {getTranslatedString('tenantPortal.paysafe.depositInfo')}
                  </StyledAmountOutstandingP>
                )}
                <StyledAmountOutstandingP>
                  {getTranslatedString('tenantPortal.paysafe.totalAmount')}:{' '}
                  <b>{formatMoney(outstandingBalance)}</b>
                </StyledAmountOutstandingP>
                {!lockedAmount && (
                  <Field
                    name="paymentAmount"
                    render={({ field }) => (
                      <StyledInput
                        {...field}
                        type="text"
                        value={validator.normalizeMoney(field.value)}
                      />
                    )}
                  />
                )}
                {formikProps.touched.paymentAmount && formikProps.errors.paymentAmount && (
                  <StyledErrorP>{formikProps.errors.paymentAmount}</StyledErrorP>
                )}
              </StyledSection>
              <StyledCardSection>
                <StyledH4>{getTranslatedString('paymentDetails')}</StyledH4>
                {/* TODO get clarification on what kind of payment types we accept */}
                <p>{getTranslatedString('tenantPortal.paysafe.paysafeDetails')}</p>
                <Field
                  name="cardHolderName"
                  render={({ field }) => (
                    <StyledInput
                      className="fs-exclude"
                      type="text"
                      placeholder={getTranslatedString('tenantPortal.paysafe.cardholderName')}
                      {...field}
                    />
                  )}
                />
                {formikProps.touched.cardHolderName && formikProps.errors.cardHolderName && (
                  <StyledErrorP>{formikProps.errors.cardHolderName}</StyledErrorP>
                )}

                <PaysafeFields
                  setPaysafeInstance={setPaysafeInstance}
                  cardNumberError={cardNumberError}
                  cvvError={cvvError}
                  expiryDateError={expiryDateError}
                  setCardNumberError={setCardNumberError}
                  setCvvError={setCvvError}
                  setExpiryDateError={setExpiryDateError}
                />
                <Field
                  name="postalCode"
                  render={({ field }) => (
                    <StyledInput
                      className="fs-exclude"
                      {...field}
                      placeholder={getTranslatedString('tenantPortal.paysafe.postalCode')}
                      type="text"
                    />
                  )}
                />
                {formikProps.touched.postalCode && formikProps.errors.postalCode && (
                  <StyledErrorP>{formikProps.errors.postalCode}</StyledErrorP>
                )}
              </StyledCardSection>

              <StyledFormCtaContainer>
                <FlatButton
                  onClick={() => {
                    handleClose();
                    resetPaysafeFieldErrors();
                  }}
                  style={{ color: AccentColor() }}
                >
                  {getTranslatedString('close')}
                </FlatButton>
                <FlatButton
                  type="submit"
                  variant="contained"
                  disabled={paymentAuthorizing}
                  style={{
                    color: 'white',
                    backgroundColor: paymentAuthorizing ? colors.LIGHT_GRAY : AccentColor(),
                  }}
                  onClick={() => {
                    formikProps.submitForm(); // this is needed to trigger CC field validation
                  }}
                >
                  {getTranslatedString('continue')}
                </FlatButton>
              </StyledFormCtaContainer>
            </StyledForm>
          );
        }}
      </Formik>
    </StyledContainer>
  );
};
